import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  WaterDrop,
  Science,
  PlayArrow,
  Refresh,
  Save,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../utils/api';

function Settings() {
  const { devices } = useAuth();
  const [selectedDevice, setSelectedDevice] = useState('');
  const [loading, setLoading] = useState(false);
  const [latestLoading, setLatestLoading] = useState(false);
  const [latestLoadedAt, setLatestLoadedAt] = useState(null);
  const [latestLoadError, setLatestLoadError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states for different settings
  const [setpoints, setSetpoints] = useState({
    ph: 7.0,
    ec: 2.0
  });
  
  const [pumpSettings, setPumpSettings] = useState({
    ph: {
      doseTime: 5,
      waitTime: 30,
      mode: 'AUTO',
      retryLimit: 3
    },
    ec: {
      doseTime: 5,
      waitTime: 30,
      mode: 'AUTO',
      retryLimit: 3
    }
  });
  
  const [systemSettings, setSystemSettings] = useState({
    automationEnabled: true,
    transmissionInterval: 60,
    manualMode: false
  });

  // Add pump status state for toggles
  const [pumpStatus, setPumpStatus] = useState({
    ph: false,
    ec1: false,
    ec2: false,
    all: false
  });

  const [irrigationSettings, setIrrigationSettings] = useState({
    enabled: true,
    timeOffsetMin: 0
  });

  const [irrigationSchedules, setIrrigationSchedules] = useState([]);
  const [irrigationSchedule, setIrrigationSchedule] = useState({
    idx: 1,
    enabled: true,
    start: '06:00',
    end: '06:15',
    ch2: true,
    ch3: false,
    ch4: false
  });

  // Fertigation: dosing device with a single irrigation relay + 8 schedules (no channel mask)
  const [fertigationIrrigation, setFertigationIrrigation] = useState({
    manualMode: false,
    manualState: false,
    active: false
  });
  const [fertigationSchedules, setFertigationSchedules] = useState([]);
  const [fertigationSchedule, setFertigationSchedule] = useState({
    idx: 1,
    enabled: true,
    start: '06:00',
    end: '06:15'
  });

  // Initialize with first device
  useEffect(() => {
    if (devices && devices.length > 0 && !selectedDevice) {
      setSelectedDevice(devices[0].deviceId);
    }
  }, [devices, selectedDevice]);

  const selectedDeviceObj = devices?.find(d => d.deviceId === selectedDevice);
  const isTds = selectedDeviceObj?.ecUnit === 'TDS';
  const isIrrigation = selectedDeviceObj?.deviceType === 'irrigation';
  const isFertigation = selectedDeviceObj?.deviceType === 'fertigation';

  const normalizeIrrigationSchedule = useCallback((schedule) => {
    const idx = typeof schedule?.idx === 'number' ? schedule.idx : 0;
    const enabled = !!schedule?.enabled;
    const start = typeof schedule?.start === 'string' ? schedule.start : '00:00';
    const end = typeof schedule?.end === 'string' ? schedule.end : '00:00';
    const mask = typeof schedule?.mask === 'number' ? schedule.mask : 0;
    return { idx, enabled, start, end, mask };
  }, []);

  const scheduleToForm = useCallback((schedule) => {
    const normalized = normalizeIrrigationSchedule(schedule);
    return {
      idx: normalized.idx + 1,
      enabled: normalized.enabled,
      start: normalized.start,
      end: normalized.end,
      ch2: (normalized.mask & 1) === 1,
      ch3: (normalized.mask & 2) === 2,
      ch4: (normalized.mask & 4) === 4
    };
  }, [normalizeIrrigationSchedule]);

  const loadLatestSettings = useCallback(async () => {
    if (!selectedDevice) return;
    try {
      setLatestLoading(true);
      setLatestLoadError(null);

      const response = await api.get(`/data/${selectedDevice}/latest`);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load latest settings');
      }

      const latest = response.data.data;
      const loadedAt = latest?.timestamp ? new Date(latest.timestamp).toLocaleString() : new Date().toLocaleString();
      setLatestLoadedAt(loadedAt);

      if (latest?.deviceType === 'irrigation') {
        const irrigation = latest?.irrigation || {};
        const schedules = Array.isArray(irrigation.schedules) ? irrigation.schedules : [];
        const normalizedSchedules = schedules.map(normalizeIrrigationSchedule);

        setIrrigationSettings({
          enabled: irrigation.enabled ?? false,
          timeOffsetMin: typeof irrigation.timeOffsetMin === 'number' ? irrigation.timeOffsetMin : 0
        });

        setIrrigationSchedules(normalizedSchedules);

        const currentUiIdx = typeof irrigationSchedule.idx === 'number' ? irrigationSchedule.idx : 1;
        const currentFirmwareIdx = Math.max(0, currentUiIdx - 1);
        const selectedFromLatest =
          normalizedSchedules.find((s) => s.idx === currentFirmwareIdx) ||
          normalizedSchedules.find((s) => s.idx === 0) ||
          normalizedSchedules[0] ||
          { idx: currentFirmwareIdx, enabled: false, start: '00:00', end: '00:00', mask: 0 };

        setIrrigationSchedule(scheduleToForm(selectedFromLatest));
        return;
      }

      if (latest?.deviceType === 'dosing' || latest?.deviceType === 'fertigation') {
        setSetpoints((prev) => ({
          ph: latest?.ph?.setpoint !== undefined ? parseFloat(latest.ph.setpoint) : prev.ph,
          ec: latest?.ec?.setpoint !== undefined ? parseFloat(latest.ec.setpoint) : prev.ec
        }));

        setPumpSettings((prev) => ({
          ph: {
            doseTime: latest?.ph?.pump?.dose_time !== undefined ? parseFloat(latest.ph.pump.dose_time) : prev.ph.doseTime,
            waitTime: latest?.ph?.pump?.wait_time !== undefined ? parseFloat(latest.ph.pump.wait_time) : prev.ph.waitTime,
            mode: latest?.ph?.pump?.mode !== undefined ? String(latest.ph.pump.mode) : prev.ph.mode,
            retryLimit: latest?.ph?.retry?.limit !== undefined ? parseFloat(latest.ph.retry.limit) : prev.ph.retryLimit
          },
          ec: {
            doseTime: latest?.ec?.pump?.dose_time !== undefined ? parseFloat(latest.ec.pump.dose_time) : prev.ec.doseTime,
            waitTime: latest?.ec?.pump?.wait_time !== undefined ? parseFloat(latest.ec.pump.wait_time) : prev.ec.waitTime,
            mode: latest?.ec?.pump?.mode !== undefined ? String(latest.ec.pump.mode) : prev.ec.mode,
            retryLimit: latest?.ec?.retry?.limit !== undefined ? parseFloat(latest.ec.retry.limit) : prev.ec.retryLimit
          }
        }));
      }

      if (latest?.deviceType === 'fertigation') {
        const irrigation = latest?.irrigation || {};
        const schedules = Array.isArray(irrigation.schedules) ? irrigation.schedules : [];

        setFertigationIrrigation({
          manualMode: !!irrigation.manualMode,
          manualState: !!irrigation.manualState,
          active: !!irrigation.active
        });

        setFertigationSchedules(schedules);

        const currentUiIdx = typeof fertigationSchedule.idx === 'number' ? fertigationSchedule.idx : 1;
        const currentFirmwareIdx = Math.max(0, currentUiIdx - 1);
        const selectedFromLatest =
          schedules.find((s) => s.idx === currentFirmwareIdx) ||
          schedules.find((s) => s.idx === 0) ||
          schedules[0] ||
          { idx: currentFirmwareIdx, enabled: false, start: '00:00', end: '00:00' };

        setFertigationSchedule({
          idx: selectedFromLatest.idx + 1,
          enabled: !!selectedFromLatest.enabled,
          start: selectedFromLatest.start || '00:00',
          end: selectedFromLatest.end || '00:00'
        });
      }
    } catch (error) {
      setLatestLoadError(error.message || 'Failed to load latest settings');
    } finally {
      setLatestLoading(false);
    }
  }, [normalizeIrrigationSchedule, irrigationSchedule.idx, scheduleToForm, selectedDevice, fertigationSchedule.idx]);

  useEffect(() => {
    if (!selectedDevice) return;
    loadLatestSettings();
  }, [selectedDevice, loadLatestSettings]);

  // Send command to device
  const sendCommand = async (command, value, extra = {}) => {
    if (!selectedDevice) {
      showSnackbar('Please select a device first', 'error');
      return;
    }

    try {
      setLoading(true);
      
      const response = await api.post('/devices/command', {
        deviceId: selectedDevice,
        command: command,
        ...(value !== undefined ? { value } : {}),
        ...extra
      });

      if (response.data.success) {
        showSnackbar(`Command ${command} sent successfully`, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to send command');
      }
    } catch (error) {
      console.error('Error sending command:', error);
      showSnackbar(`Failed to send command: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle setpoint changes
  const handleSetpointChange = (type, value) => {
    setSetpoints(prev => ({ ...prev, [type]: parseFloat(value) }));
  };

  const saveSetpoint = (type) => {
    const command = type === 'ph' ? 'PH_SETPOINT' : 'EC_SETPOINT';
    const value = setpoints[type];
    sendCommand(command, value);
  };

  // Handle pump settings changes
  const handlePumpSettingChange = (type, setting, value) => {
    setPumpSettings(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [setting]: setting === 'mode' ? value : parseFloat(value)
      }
    }));
  };

  const savePumpSetting = (type, setting) => {
    const commandMap = {
      ph: {
        doseTime: 'PH_DOSE_TIME',
        waitTime: 'PH_WAIT_TIME',
        mode: 'PH_PUMP_MODE',
        retryLimit: 'PH_RETRY_LIMIT'
      },
      ec: {
        doseTime: 'EC_DOSE_TIME',
        waitTime: 'EC_WAIT_TIME',
        mode: 'EC_PUMP_MODE',
        retryLimit: 'EC_RETRY_LIMIT'
      }
    };
    
    const command = commandMap[type][setting];
    const value = pumpSettings[type][setting];
    sendCommand(command, value);
  };

  // Handle system settings

  const toggleManualMode = () => {
    const newState = !systemSettings.manualMode;
    setSystemSettings(prev => ({ ...prev, manualMode: newState }));
    sendCommand('MANUAL_MODE', newState ? 1 : 0);
  };

  const saveTransmissionInterval = () => {
    sendCommand('SET_TRANSMISSION_INTERVAL', systemSettings.transmissionInterval);
  };

  const resetSystem = () => {
    if (window.confirm('Are you sure you want to reset the system? This action cannot be undone.')) {
      sendCommand('RESET_SYSTEM', 1);
    }
  };

  // Manual pump controls with toggle functionality
  const manualPumpControl = (pumpType, isOn) => {
    const commandMap = {
      'ph': 'MANUAL_MOTOR3',
      'ec1': 'MANUAL_MOTOR1', 
      'ec2': 'MANUAL_MOTOR2',
      'all': 'MANUAL_ALL'
    };
    
    // Update pump status
    setPumpStatus(prev => ({ ...prev, [pumpType]: isOn }));
    
    // Send command (1 for on, 0 for off)
    sendCommand(commandMap[pumpType], isOn ? 1 : 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SettingsIcon sx={{ fontSize: '2rem', color: '#41ab5d' }} />
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure system settings and device controls
        </Typography>
      </Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Select Device</InputLabel>
          <Select
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            label="Select Device"
          >
            {devices?.map(device => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.deviceId} - {device.location || 'Unknown Location'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={loadLatestSettings}
            disabled={!selectedDevice || latestLoading}
            startIcon={<Refresh />}
          >
            Load Latest
          </Button>
        </Box>
      </Box>

      {latestLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {latestLoadError}
        </Alert>
      )}

      {latestLoadedAt && !latestLoadError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Latest settings loaded: {latestLoadedAt}
        </Alert>
      )}

      <Grid container spacing={3}>
        {isIrrigation ? (
          <>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Irrigation Controls"
                  avatar={<WaterDrop sx={{ color: '#0077cc' }} />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={irrigationSettings.enabled}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setIrrigationSettings((prev) => ({ ...prev, enabled: next }));
                              sendCommand('IRR_SET_GLOBAL_ENABLE', undefined, { enabled: next });
                            }}
                            disabled={loading}
                          />
                        }
                        label="Enable irrigation"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          label="Time Offset"
                          type="number"
                          value={irrigationSettings.timeOffsetMin}
                          onChange={(e) =>
                            setIrrigationSettings((prev) => ({
                              ...prev,
                              timeOffsetMin: parseInt(e.target.value, 10)
                            }))
                          }
                          InputProps={{
                            endAdornment: <InputAdornment position="end">min</InputAdornment>
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          variant="contained"
                          onClick={() =>
                            sendCommand('IRR_SET_TIME_OFFSET_MIN', irrigationSettings.timeOffsetMin)
                          }
                          disabled={loading}
                          startIcon={<Save />}
                        >
                          Set
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => sendCommand('IRR_CLEAR_NVS')}
                        disabled={loading}
                        startIcon={<Refresh />}
                        fullWidth
                      >
                        Clear Irrigation NVS
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => sendCommand('IRR_RESET_WIFI_NVS')}
                        disabled={loading}
                        startIcon={<Refresh />}
                        fullWidth
                      >
                        Clear WiFi NVS
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Schedule"
                  avatar={<PlayArrow sx={{ color: '#10b981' }} />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Loaded schedules: {irrigationSchedules.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Schedule Index</InputLabel>
                        <Select
                          value={irrigationSchedule.idx}
                          onChange={(e) => {
                            const uiIdx = parseInt(e.target.value, 10);
                            const firmwareIdx = Math.max(0, uiIdx - 1);
                            const fromLatest = irrigationSchedules.find((s) => s.idx === firmwareIdx);
                            if (fromLatest) {
                              setIrrigationSchedule(scheduleToForm(fromLatest));
                            } else {
                              setIrrigationSchedule((prev) => ({ ...prev, idx: uiIdx }));
                            }
                          }}
                          label="Schedule Index"
                        >
                          {Array.from({ length: 8 }).map((_, i) => {
                            const uiIdx = i + 1;
                            return (
                              <MenuItem key={uiIdx} value={uiIdx}>
                                {uiIdx}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={irrigationSchedule.enabled}
                            onChange={(e) =>
                              setIrrigationSchedule((prev) => ({
                                ...prev,
                                enabled: e.target.checked
                              }))
                            }
                            disabled={loading}
                          />
                        }
                        label="Enabled"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="Start"
                        type="time"
                        value={irrigationSchedule.start}
                        onChange={(e) =>
                          setIrrigationSchedule((prev) => ({ ...prev, start: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="End"
                        type="time"
                        value={irrigationSchedule.end}
                        onChange={(e) =>
                          setIrrigationSchedule((prev) => ({ ...prev, end: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Channels
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={irrigationSchedule.ch2}
                              onChange={(e) =>
                                setIrrigationSchedule((prev) => ({
                                  ...prev,
                                  ch2: e.target.checked
                                }))
                              }
                              disabled={loading}
                            />
                          }
                          label="CH2"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={irrigationSchedule.ch3}
                              onChange={(e) =>
                                setIrrigationSchedule((prev) => ({
                                  ...prev,
                                  ch3: e.target.checked
                                }))
                              }
                              disabled={loading}
                            />
                          }
                          label="CH3"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={irrigationSchedule.ch4}
                              onChange={(e) =>
                                setIrrigationSchedule((prev) => ({
                                  ...prev,
                                  ch4: e.target.checked
                                }))
                              }
                              disabled={loading}
                            />
                          }
                          label="CH4"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          const mask =
                            (irrigationSchedule.ch2 ? 1 : 0) +
                            (irrigationSchedule.ch3 ? 2 : 0) +
                            (irrigationSchedule.ch4 ? 4 : 0);

                          sendCommand('IRR_SET_SCHEDULE', undefined, {
                            idx: Math.max(0, irrigationSchedule.idx - 1),
                            enabled: irrigationSchedule.enabled,
                            start: irrigationSchedule.start,
                            end: irrigationSchedule.end,
                            mask
                          });
                        }}
                        disabled={loading}
                        startIcon={<Save />}
                        fullWidth
                      >
                        Save Schedule
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : (<>
        {/* Setpoints Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Setpoints Configuration"
              avatar={<WaterDrop sx={{ color: '#0077cc' }} />}
            />
            <CardContent>
              <Grid container spacing={3}>
                {/* pH Setpoint */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="pH Setpoint"
                      type="number"
                      value={setpoints.ph}
                      onChange={(e) => handleSetpointChange('ph', e.target.value)}
                      inputProps={{ min: 0, max: 14, step: 0.1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">pH</InputAdornment>
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => saveSetpoint('ph')}
                      disabled={loading}
                      startIcon={<Save />}
                      sx={{ bgcolor: '#0077cc' }}
                    >
                      Set
                    </Button>
                  </Box>
                </Grid>
                
                {/* EC/TDS Setpoint */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label={isTds ? 'TDS Setpoint' : 'EC Setpoint'}
                      type="number"
                      value={setpoints.ec}
                      onChange={(e) => handleSetpointChange('ec', e.target.value)}
                      inputProps={{ min: 0, max: 10, step: 0.1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{isTds ? 'ppm' : 'μS/cm'}</InputAdornment>
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => saveSetpoint('ec')}
                      disabled={loading}
                      startIcon={<Save />}
                      sx={{ bgcolor: '#f59e0b' }}
                    >
                      Set
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* System Controls */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="System Controls"
              avatar={<SettingsIcon sx={{ color: '#10b981' }} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={systemSettings.manualMode}
                        onChange={toggleManualMode}
                        disabled={loading}
                      />
                    }
                    label="Manual Mode"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Transmission Interval"
                      type="number"
                      value={systemSettings.transmissionInterval}
                      onChange={(e) => setSystemSettings(prev => ({ 
                        ...prev, 
                        transmissionInterval: parseInt(e.target.value) 
                      }))}
                      inputProps={{ min: 10, max: 3600 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">sec</InputAdornment>
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      onClick={saveTransmissionInterval}
                      disabled={loading}
                      startIcon={<Save />}
                    >
                      Set
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={resetSystem}
                    disabled={loading}
                    startIcon={<Refresh />}
                    fullWidth
                  >
                    Reset System
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* pH Pump Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="pH Pump Configuration"
              avatar={<WaterDrop sx={{ color: '#0077cc' }} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Dose Time"
                      type="number"
                      value={pumpSettings.ph.doseTime}
                      onChange={(e) => handlePumpSettingChange('ph', 'doseTime', e.target.value)}
                      inputProps={{ min: 1, max: 60 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">sec</InputAdornment>
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ph', 'doseTime')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Wait Time"
                      type="number"
                      value={pumpSettings.ph.waitTime}
                      onChange={(e) => handlePumpSettingChange('ph', 'waitTime', e.target.value)}
                      inputProps={{ min: 1, max: 120 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ph', 'waitTime')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Pump Mode</InputLabel>
                      <Select
                        value={pumpSettings.ph.mode}
                        onChange={(e) => handlePumpSettingChange('ph', 'mode', e.target.value)}
                        label="Pump Mode"
                      >
                        <MenuItem value="AUTO">Auto</MenuItem>
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="OFF">Off</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => savePumpSetting('ph', 'mode')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Retry Limit"
                      type="number"
                      value={pumpSettings.ph.retryLimit}
                      onChange={(e) => handlePumpSettingChange('ph', 'retryLimit', e.target.value)}
                      inputProps={{ min: 0, max: 10 }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ph', 'retryLimit')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* EC/TDS Pump Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title={isTds ? 'TDS Pump Configuration' : 'EC Pump Configuration'}
              avatar={<Science sx={{ color: '#f59e0b' }} />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Dose Time"
                      type="number"
                      value={pumpSettings.ec.doseTime}
                      onChange={(e) => handlePumpSettingChange('ec', 'doseTime', e.target.value)}
                      inputProps={{ min: 1, max: 60 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">sec</InputAdornment>
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ec', 'doseTime')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Wait Time"
                      type="number"
                      value={pumpSettings.ec.waitTime}
                      onChange={(e) => handlePumpSettingChange('ec', 'waitTime', e.target.value)}
                      inputProps={{ min: 1, max: 120 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ec', 'waitTime')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Pump Mode</InputLabel>
                      <Select
                        value={pumpSettings.ec.mode}
                        onChange={(e) => handlePumpSettingChange('ec', 'mode', e.target.value)}
                        label="Pump Mode"
                      >
                        <MenuItem value="AUTO">Auto</MenuItem>
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="OFF">Off</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => savePumpSetting('ec', 'mode')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Retry Limit"
                      type="number"
                      value={pumpSettings.ec.retryLimit}
                      onChange={(e) => handlePumpSettingChange('ec', 'retryLimit', e.target.value)}
                      inputProps={{ min: 0, max: 10 }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      onClick={() => savePumpSetting('ec', 'retryLimit')}
                      disabled={loading}
                      size="small"
                    >
                      <Save />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Manual Pump Controls */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Manual Pump Controls"
              avatar={<PlayArrow sx={{ color: '#ef4444' }} />}
            />
            <CardContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Manual pump controls will override automatic dosing. Use with caution.
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      pH Pump
                    </Typography>
                    <Switch
                      checked={pumpStatus.ph}
                      onChange={(e) => manualPumpControl('ph', e.target.checked)}
                      disabled={loading || !systemSettings.manualMode}
                      color="primary"
                      size="medium"
                    />
                    <Typography variant="caption" color={pumpStatus.ph ? 'success.main' : 'text.secondary'}>
                      {pumpStatus.ph ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isTds ? 'TDS Pump 1' : 'EC Pump 1'}
                    </Typography>
                    <Switch
                      checked={pumpStatus.ec1}
                      onChange={(e) => manualPumpControl('ec1', e.target.checked)}
                      disabled={loading || !systemSettings.manualMode}
                      color="primary"
                      size="medium"
                    />
                    <Typography variant="caption" color={pumpStatus.ec1 ? 'success.main' : 'text.secondary'}>
                      {pumpStatus.ec1 ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isTds ? 'TDS Pump 2' : 'EC Pump 2'}
                    </Typography>
                    <Switch
                      checked={pumpStatus.ec2}
                      onChange={(e) => manualPumpControl('ec2', e.target.checked)}
                      disabled={loading || !systemSettings.manualMode}
                      color="primary"
                      size="medium"
                    />
                    <Typography variant="caption" color={pumpStatus.ec2 ? 'success.main' : 'text.secondary'}>
                      {pumpStatus.ec2 ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6} sm={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                      All Pumps
                    </Typography>
                    <Switch
                      checked={pumpStatus.all}
                      onChange={(e) => manualPumpControl('all', e.target.checked)}
                      disabled={loading || !systemSettings.manualMode}
                      color="error"
                      size="medium"
                    />
                    <Typography variant="caption" color={pumpStatus.all ? 'error.main' : 'text.secondary'}>
                      {pumpStatus.all ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {!systemSettings.manualMode && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Enable Manual Mode to use pump controls
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {isFertigation && (
          <>
            {/* Fertigation Irrigation Controls */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Irrigation Controls (Fertigation)"
                  avatar={<WaterDrop sx={{ color: '#0077cc' }} />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={fertigationIrrigation.manualMode}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setFertigationIrrigation((prev) => ({ ...prev, manualMode: next }));
                              sendCommand('IRRIG_SET_MANUAL_MODE', undefined, { enabled: next });
                            }}
                            disabled={loading}
                          />
                        }
                        label="Manual mode (overrides schedule)"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Relay state
                        </Typography>
                        <Switch
                          checked={fertigationIrrigation.manualState}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setFertigationIrrigation((prev) => ({ ...prev, manualState: next }));
                            sendCommand('IRRIG_SET_MANUAL_STATE', undefined, { state: next });
                          }}
                          disabled={loading || !fertigationIrrigation.manualMode}
                          color="primary"
                        />
                        {!fertigationIrrigation.manualMode && (
                          <Typography variant="caption" color="text.secondary">
                            Enable manual mode to toggle the relay directly
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Chip
                        label={fertigationIrrigation.active ? 'Relay currently ON' : 'Relay currently OFF'}
                        size="small"
                        sx={{
                          bgcolor: fertigationIrrigation.active ? '#e8f5e8' : '#f9fafb',
                          color: fertigationIrrigation.active ? '#2e7d32' : '#666666',
                          border: '1px solid #e5e7eb',
                          fontWeight: 500
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Fertigation Schedule */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader
                  title="Irrigation Schedule (Fertigation)"
                  avatar={<PlayArrow sx={{ color: '#10b981' }} />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Loaded schedules: {fertigationSchedules.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Schedule Index</InputLabel>
                        <Select
                          value={fertigationSchedule.idx}
                          onChange={(e) => {
                            const uiIdx = parseInt(e.target.value, 10);
                            const firmwareIdx = Math.max(0, uiIdx - 1);
                            const fromLatest = fertigationSchedules.find((s) => s.idx === firmwareIdx);
                            if (fromLatest) {
                              setFertigationSchedule({
                                idx: fromLatest.idx + 1,
                                enabled: !!fromLatest.enabled,
                                start: fromLatest.start || '00:00',
                                end: fromLatest.end || '00:00'
                              });
                            } else {
                              setFertigationSchedule((prev) => ({ ...prev, idx: uiIdx }));
                            }
                          }}
                          label="Schedule Index"
                        >
                          {Array.from({ length: 8 }).map((_, i) => {
                            const uiIdx = i + 1;
                            return (
                              <MenuItem key={uiIdx} value={uiIdx}>
                                {uiIdx}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={fertigationSchedule.enabled}
                            onChange={(e) =>
                              setFertigationSchedule((prev) => ({
                                ...prev,
                                enabled: e.target.checked
                              }))
                            }
                            disabled={loading}
                          />
                        }
                        label="Enabled"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="Start"
                        type="time"
                        value={fertigationSchedule.start}
                        onChange={(e) =>
                          setFertigationSchedule((prev) => ({ ...prev, start: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="End"
                        type="time"
                        value={fertigationSchedule.end}
                        onChange={(e) =>
                          setFertigationSchedule((prev) => ({ ...prev, end: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        size="small"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          sendCommand('IRR_SET_SCHEDULE', undefined, {
                            idx: Math.max(0, fertigationSchedule.idx - 1),
                            enabled: fertigationSchedule.enabled,
                            start: fertigationSchedule.start,
                            end: fertigationSchedule.end
                          });
                        }}
                        disabled={loading}
                        startIcon={<Save />}
                        fullWidth
                      >
                        Save Schedule
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
        </>)}
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
